import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchBugs, Bug, BugStatus, BugSeverity } from '../../../store/bugSlice';

const STATUS_COLORS: Record<BugStatus, string> = {
  reported: 'bg-gray-100 text-gray-700',
  acknowledged: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  testing: 'bg-purple-100 text-purple-700',
  fixed: 'bg-green-100 text-green-700',
  verified: 'bg-teal-100 text-teal-700',
  closed: 'bg-gray-200 text-gray-500',
  wont_fix: 'bg-gray-200 text-gray-500',
  duplicate: 'bg-gray-200 text-gray-500',
};

const SEVERITY_COLORS: Record<BugSeverity, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
};

const SEVERITY_DOT: Record<BugSeverity, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-gray-400',
};

interface Props {
  projectId?: string;
  onSelectBug?: (bugId: string) => void;
  onReportBug?: () => void;
}

const BugInbox: React.FC<Props> = ({ projectId, onSelectBug, onReportBug }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { bugs, loading, error, total } = useAppSelector(s => s.bugs);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(fetchBugs({
      status: statusFilter || undefined,
      priority: severityFilter || undefined,
      projectId: projectId || undefined,
      page: String(page),
    }));
  }, [dispatch, statusFilter, severityFilter, projectId, page]);

  const filtered = search
    ? bugs.filter(b => b.title.toLowerCase().includes(search.toLowerCase()))
    : bugs;

  const openCount = bugs.filter(b => !['closed', 'wont_fix', 'duplicate'].includes(b.status)).length;
  const criticalCount = bugs.filter(b => b.impact.severity === 'critical' && !['closed', 'fixed', 'verified'].includes(b.status)).length;

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

      {/* Stats bar — only shown when bugs exist */}
      {bugs.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">{total}</div>
            <div className="text-xs text-gray-500 mt-0.5">Total</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{openCount}</div>
            <div className="text-xs text-gray-500 mt-0.5">Open</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{criticalCount}</div>
            <div className="text-xs text-gray-500 mt-0.5">Critical</div>
          </div>
        </div>
      )}

      {/* Filters & search — only shown when bugs exist */}
      {bugs.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search bugs…"
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
          >
            <option value="">All Status</option>
            {(['reported', 'acknowledged', 'in_progress', 'testing', 'fixed', 'verified', 'closed', 'wont_fix'] as BugStatus[]).map(s => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
          <select
            value={severityFilter}
            onChange={e => { setSeverityFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
          >
            <option value="">All Severity</option>
            {(['critical', 'high', 'medium', 'low'] as BugSeverity[]).map(s => (
              <option key={s} value={s} className="capitalize">{s}</option>
            ))}
          </select>
          <button
            onClick={() => onReportBug ? onReportBug() : navigate('/bugs/report')}
            className="bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-red-700 ml-auto"
          >
            Report Bug
          </button>
        </div>
      )}

      {/* Bug list */}
      {loading && bugs.length === 0 ? (
        <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
      ) : bugs.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
          <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44a23.916 23.916 0 001.152 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No bugs reported</h2>
          <p className="text-sm text-gray-500 max-w-sm mb-8">Bugs are tracked here as they're discovered. When something breaks, report it here to keep work visible.</p>
          <button
            onClick={() => onReportBug ? onReportBug() : navigate('/bugs/report')}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors"
          >
            Report a Bug
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-sm font-medium">No results for this filter.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((bug: Bug) => (
            <div
              key={bug._id}
              onClick={() => onSelectBug ? onSelectBug(bug._id) : navigate(`/bugs/${bug._id}`)}
              className="bg-white border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-3">
                {/* Severity indicator */}
                <div className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${SEVERITY_DOT[bug.impact.severity]}`} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[bug.status]}`}>
                      {bug.status.replace('_', ' ')}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${SEVERITY_COLORS[bug.impact.severity]}`}>
                      {bug.impact.severity}
                    </span>
                    {bug.priority !== bug.impact.severity && (
                      <span className="text-xs text-gray-400">P: {bug.priority}</span>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 truncate">{bug.title}</h3>
                  <div className="flex gap-3 mt-1 text-xs text-gray-400">
                    <span>By {bug.reportedBy.name}</span>
                    <span>·</span>
                    <span>{new Date(bug.reportedAt).toLocaleDateString()}</span>
                    {bug.commentsCount > 0 && (
                      <>
                        <span>·</span>
                        <span>{bug.commentsCount} comment{bug.commentsCount !== 1 ? 's' : ''}</span>
                      </>
                    )}
                    {bug.assignedTo.length > 0 && (
                      <>
                        <span>·</span>
                        <span>→ {bug.assignedTo.map(a => a.name).join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-gray-600">Page {page}</span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={bugs.length < 20}
            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BugInbox;
